import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  jsonLd?: object;
}

const defaultTitle = "Gamification Strategy Generator";
const defaultDescription = "Get a personalized, AI-powered gamification strategy for your product in 10 minutes. Only $5 with complete implementation package and 30-day guarantee.";
const siteUrl = "https://gamifyme-scorecard.lovable.app";
const defaultOgImage = `${siteUrl}/og-image.png`;

const SEO = ({
  title,
  description = defaultDescription,
  keywords = "gamification, app engagement, user retention, game mechanics, mobile app strategy, AI strategy, app development",
  canonicalUrl,
  ogImage = defaultOgImage,
  ogType = 'website',
  jsonLd,
}: SEOProps) => {
  const fullTitle = title 
    ? `${title} | ${defaultTitle}` 
    : `${defaultTitle} - Get Your AI-Powered Strategy in Minutes`;
  
  const canonical = canonicalUrl || siteUrl;

  // Default organization schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Gamification Strategy Generator",
    "url": siteUrl,
    "logo": `${siteUrl}/favicon.ico`,
    "description": defaultDescription,
    "sameAs": []
  };

  // Product schema for pricing page
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Complete Gamification Strategy Package",
    "description": "AI-powered personalized gamification strategy with implementation guidelines, technical specifications, and design guidelines.",
    "offers": {
      "@type": "Offer",
      "price": "79.00",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": "2027-12-31"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "500"
    }
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Gamification Strategy Generator" />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={canonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={defaultTitle} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#7c3aed" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLd || organizationSchema)}
      </script>
    </Helmet>
  );
};

export { SEO, type SEOProps };
export default SEO;
